import { IncomingMessage } from 'http';
import { Where } from 'payload';

import { publicConfig } from '@public-config';

import {
    Component,
    ComponentFacetKey,
    ComponentQuery,
    ComponentSearchScore,
    ComponentType,
    ComponentVisibility,
    DCCatalogStatus,
    ListResponse,
    SortType,
    Team,
} from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { CryptoHelpers } from 'helpers/CryptoHelpers';
import { ServiceHelpers } from 'helpers/ServiceHelpers';

import { state as currentUserState } from 'state/current-user';

import { ApiService } from './ApiService';
import { authHeaders, PayloadSDK } from './PayloadSDK';

export type ComponentListType = {
    depth?: number;
    page?: number;
    limit?: number;
    sort?: string;
    manufacturer?: string;
    manufacturerOnly?: boolean;
    noManufacturer?: boolean;
    team?: string;
    visibility?: ComponentVisibility;
    showDeleted?: boolean;
    showArchived?: boolean;
    showPrivate?: boolean;
    type?: ComponentType;
};

// safe to use depth=1 b/c we set maxDepth on relationship fields
class ComponentService {
    static controller: AbortController | null = null;

    static navigate = ServiceHelpers.createNavigator(ComponentHelpers.urls);

    static list = async ({
        depth = 1,
        page = 1,
        limit = 99,
        sort = '-createdAt',
        manufacturer,
        manufacturerOnly,
        noManufacturer,
        team,
        visibility = ComponentVisibility.PUBLIC,
        showArchived,
        showDeleted,
        showPrivate,
        type,
    }: ComponentListType = {}) => {
        const where: Where = { and: [] };

        if (manufacturer && manufacturerOnly) {
            where.and!.push({ manufacturer: { equals: manufacturer } });
        } else if (manufacturer) {
            where.and!.push({
                or: [
                    {
                        manufacturer: { equals: manufacturer },
                    },
                    {
                        distributors: { equals: manufacturer },
                    },
                    {
                        'distributorPrices.distributor': { equals: manufacturer },
                    },
                ],
            });
        }

        if (noManufacturer) {
            where.and!.push({ manufacturer: { equals: null } });
        }

        if (team) {
            where.and!.push({ team: { equals: team } });
        }

        if (visibility && !(showPrivate && visibility === ComponentVisibility.PUBLIC)) {
            where.and!.push({ visibility: { equals: visibility } });
        }

        if (!showArchived) {
            where.and!.push({ archivedAt: { exists: false } });
        }

        if (!showDeleted) {
            where.and!.push({ deletedAt: { exists: false } });
        }

        if (type) {
            where.and!.push({ type: { equals: type } });
        }

        return (await PayloadSDK.find({
            collection: 'components',
            depth,
            page,
            limit,
            sort,
            where,
        })) as unknown as ListResponse<Component>;
    };

    static getRecentlyAdded = async (limit = 8) => {
        return await ComponentService.list({ sort: '-createdAt', limit, visibility: ComponentVisibility.PUBLIC });
    };

    static search = async (
        {
            sort = SortType.RELEVANCE,
            limit,
            customWeights,
            facets,
            ...query
        }: Partial<ComponentQuery> & {
            limit?: number;
            customWeights?: Partial<Record<ComponentSearchScore, number>>;
            facets?: ComponentFacetKey[];
        } = {},
        page = 0,
    ) => {
        const cleanQuery = ComponentHelpers.getSearchRequest(query);

        const params: Record<string, string> = {
            query: JSON.stringify(cleanQuery),
            page: page.toString(),
            sort: sort,
        };

        if (limit) {
            params.limit = limit.toString();
        }

        if (customWeights) {
            params.customWeights = JSON.stringify(customWeights);
        }

        if (facets?.length) {
            params.facets = facets.join(',');
        }

        if (currentUserState.user?.internal) {
            console.info('components/search query', {
                query: cleanQuery,
                page,
                sort,
                customWeights: customWeights && { ...customWeights },
            });
        }

        const queryString = new URLSearchParams(params).toString();

        const result = await cachedGet(`${publicConfig.urls.api}/components/search?${queryString}`);

        const { docs = [] } = result;

        const combinedDocs = docs.map((doc: any, index: number) => ({
            ...doc,
            __position: 48 * page + (index + 1),
        }));

        return {
            ...result,
            docs: combinedDocs,
        };
    };

    static create = async (payload: any, depth: number = 1) => {
        const response = await ApiService.post(`${publicConfig.urls.api}/components?depth=${depth}`, payload).finally(
            clearSearchCache,
        );

        return response?.doc;
    };

    static get = async (
        componentId: string,
        {
            depth = 1,
            skipAccessCheck = false,
            req,
        }: { depth?: number; skipAccessCheck?: boolean; req?: IncomingMessage } = {},
    ) => {
        const hash = skipAccessCheck ? CryptoHelpers.unsafeSimpleHash(componentId) : '';

        return ApiService.get(`${publicConfig.urls.api}/components/${componentId}?depth=${depth}&hash=${hash}`, {
            req,
        });
    };

    static getMultiple = async (componentIds: string[], req?: IncomingMessage) => {
        const result = await PayloadSDK.find(
            {
                collection: 'components',
                where: {
                    id: {
                        in: componentIds,
                    },
                },
                pagination: false,
            },
            { headers: authHeaders(req) },
        );

        return {
            ...result,
            docs: result.docs as unknown as Component[],
        };
    };

    static delete = async (componentId: string) => {
        return ApiService.post(`${publicConfig.urls.api}/components/${componentId}/delete`).finally(clearSearchCache);
    };

    static getExisting = async ({
        type,
        productIdentifier,
        manufacturer,
        depth = 1,
    }: {
        type: string;
        productIdentifier: string;
        manufacturer: string;
        depth?: number;
    }) => {
        const query = [
            `where[type][equals]=${type}`,
            `where[productIdentifier][equals]=${productIdentifier}`,
            `where[manufacturer][equals]=${manufacturer}`,
        ];
        const response = await ApiService.get(
            `${publicConfig.urls.api}/components?${query.join('&')}&depth=${depth}&limit=1`,
        );

        return response?.docs[0];
    };

    static getFiles = async (componentId: string, query?: ComponentVisibility) => {
        const queryString = query ? `?${query}` : '';
        return ApiService.get(`${publicConfig.urls.api}/components/${componentId}/files${queryString}`);
    };

    static update = async (componentId: string, payload: any, depth: number = 1) => {
        return await ApiService.patch(
            `${publicConfig.urls.api}/components/${componentId}?depth=${depth}`,
            payload,
        ).finally(clearSearchCache);
    };

    static updateCatalogs = async (
        componentId: string,
        catalogs: {
            dc: {
                status?: DCCatalogStatus;
                flaggedAt?: string;
                reviewedAt?: string | null;
                reviewedBy?: string | null;
            };
        },
    ) => {
        return ApiService.patch(`${publicConfig.urls.api}/components/${componentId}/catalogs`, catalogs).finally(
            clearSearchCache,
        );
    };

    static getCount = async (teamComponents?: boolean, req?: IncomingMessage) => {
        return ApiService.get(
            `${publicConfig.urls.api}/components/count${teamComponents ? `?teamComponents=true` : ''}`,
            { req },
        );
    };

    static getDistributors = async (): Promise<{ id: string; name: string }[]> => {
        return ApiService.get(`${publicConfig.urls.api}/components/distributors`);
    };

    static addDistributor = async (componentId: string, payload: { distributorId: string }) => {
        return ApiService.post(`${publicConfig.urls.api}/components/${componentId}/add-distributor`, payload).finally(
            clearSearchCache,
        );
    };

    static getProductSeries = async () => {
        return ApiService.get(`${publicConfig.urls.api}/components/product-series`);
    };

    static getComponentNames = async (manufacturerId: string) => {
        return ApiService.get(`${publicConfig.urls.api}/components/names?manufacturerId=${manufacturerId}`);
    };

    static getProductSeriesComponents = async ({
        manufacturerId,
        productSeries,
    }: {
        manufacturerId: string;
        productSeries: string;
    }) => {
        const encodedProductSeries = encodeURIComponent(productSeries.replace('/', '++--++'));

        return ApiService.get(
            `${publicConfig.urls.api}/components/product-series/${manufacturerId}/${encodedProductSeries}`,
        );
    };

    static updateMultipleComponents = async ({
        componentIds,
        update,
        newFiles,
        replaceFields,
    }: {
        componentIds: string[];
        update: Component;
        newFiles: Component['files'];
        replaceFields: string[];
    }) => {
        return ApiService.post(`${publicConfig.urls.api}/components/bulk-edit`, {
            componentIds,
            update,
            newFiles,
            replaceFields,
        }).finally(clearSearchCache);
    };

    static isTeamComponent = (component?: Component) => {
        return component && !component.manufacturer && component.visibility === ComponentVisibility.PRIVATE;
    };

    static getFeaturedProducts = async (req?: IncomingMessage): Promise<Component[]> =>
        req
            ? ApiService.get(`${publicConfig.urls.api}/components/featured-products`, { req })
            : cachedGet(`${publicConfig.urls.api}/components/featured-products`);

    static getAll = async (teamId: Team['id']) =>
        ApiService.get(`${publicConfig.urls.api}/components?where[team][equals]=${teamId}`);
}

const SEARCH_CACHE_LIFETIME = 5 * 60 * 1000;

const searchCache = new Map<string, { expiresAt: number; result: Promise<any> }>();

const cachedGet = (url: string) => {
    if (typeof window === 'undefined') {
        return ApiService.get(url);
    }

    const now = Date.now();
    const key = `${currentUserState.user?.id}:${currentUserState.user?.team}:${url}`;

    const cached = searchCache.get(key);
    if (cached && cached.expiresAt > now) {
        return cached.result;
    }

    for (const [cachedKey, { expiresAt }] of searchCache) {
        if (expiresAt <= now) searchCache.delete(cachedKey);
    }

    const result = ApiService.get(url)
        .then((result) => {
            if (result === undefined) {
                searchCache.delete(key);
            }

            return result;
        })
        .catch((error) => {
            searchCache.delete(key);
            throw error;
        });

    searchCache.set(key, { expiresAt: now + SEARCH_CACHE_LIFETIME, result });

    return result;
};

const clearSearchCache = () => {
    searchCache.clear();
};

export { ComponentService };
